import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from 'components/Panel';
import Break from 'components/Break';
import {
  FullPanel,
  SubPanel,
  SubPanelHeader,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelButtonRow,
  PanelContent
} from 'components/PanelComponents';
import {
  BlueButtonWithWriteAccess as AddButton,
  BlackBorderButtonWithWriteAccess as EditButton,
  BlueBorderButton
} from 'components/Button';
import AddRelationsContainer from 'components/Panels/AddRelationsContainer';
import SectionHeader from 'components/Panels/SectionHeader';
import ControlFieldsContainer from 'components/ControlFieldsContainer';
import {
  RESOURCE_TYPES,
  CONTROL_TYPES,
  API_FIELD_MAPPINGS,
  DEFAULT_CONTROL_FIELD_UNIT,
  DEFAULT_CONTROL_FIELDS_LIST_ORDER,
  SIDEPANEL_STATES,
  TEXTFIELD,
  DATE_FORMAT
} from 'utils/appConstants';
import moment from 'moment';
import {
  derInfoDataConverter,
  durationFormatter,
  isExternallyManaged,
  formatNumberWithPrefixAndMultiplier
} from 'utils/format';
import {
  setSidePanel,
  closeSidePanel,
  revertSidePanel
} from 'reducers/sidePanel/actions';
import { determineBack } from 'utils/determineBack';
import {
  POLL_CONTROLS_AND_RESPONSES,
  POLL_DEVICES,
  subscribe,
  unsubscribe,
  POLL_INDIVIDUAL_INDENODE,
  POLL_INDIVIDUAL_TOPONODE,
  POLL_INDENODE_DEFAULT_CONTROL,
  POLL_TOPONODE_DEFAULT_CONTROL,
  POLL_CURVES
} from 'reducers/polling/actions';
import { SEP_RN_DEFAULT_DER_CONTROL } from 'utils/appConstants';
import { TooltipRow } from 'components/Tooltip';
import Table from 'components/Table';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const controlColumnDefs = [
  {
    headerName: 'Control ID',
    field: 'controlId'
  },
  {
    headerName: 'Description',
    field: 'description'
  },
  {
    headerName: 'Status',
    field: 'statusDisplayValue'
  },
  {
    headerName: 'Start Time',
    field: 'start',
    cellRenderer: ({ value }) => moment.unix(value).format(DATE_FORMAT)
  },
  {
    headerName: 'End Time',
    field: 'duration',
    cellRenderer: row => {
      return moment
        .unix(row.data.start + row.data.duration)
        .format(DATE_FORMAT);
    }
  }
];

const childNodeColumnDefs = [
  {
    headerName: 'ID',
    field: 'topoNodeId'
  },
  {
    headerName: 'Description',
    field: 'description'
  },
  {
    headerName: 'Primacy',
    field: 'primacy'
  }
];

const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);
const disabledRemoteMessage = 'Remote nodes may not be modified';
const subName = 'NodePanel';

const NodePanel = ({ isActive }) => {
  const dispatch = useDispatch();
  const topoNodes = useSelector(state => state.topoNode.topoNodes);
  const devices = useSelector(state => state.device.devices);
  const indeNodes = useSelector(state => state.indeNode.indeNodes);
  const sidePanel = useSelector(state => state.sidePanel);
  const entity = useSelector(state => state.sidePanel.entity);
  const id = useSelector(state => state.sidePanel.id);
  const controls = useSelector(state => state.control.controls);
  const [nodeInfo, setNodeInfo] = useState({});
  const [defaultControl, setDefaultControl] = useState({
    controlTypes: {}
  });
  const [scheduledControls, setScheduledControls] = useState([]);
  const [nodeDevices, setNodeDevices] = useState([]);
  const [childNodes, setChildNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const compileNodePanelInfo = useCallback(async () => {
    const { id, entity } = sidePanel;
    let nodeInfo = {};
    let nodeDevices = [];
    let childNodes = [];
    let defaultControl = {};
    const nodeControls = controls.filter(control => control.nodeId === id);
    const isTopoNode = entity === RESOURCE_TYPES.TOPONODE;
    if (isTopoNode) {
      const node = topoNodes.find(topoNode => topoNode.topoNodeId === id);
      nodeInfo = { ...node };
      childNodes = nodeInfo.childTopoNodeIds.map(childNode =>
        topoNodes.find(topoNode => topoNode.topoNodeId === childNode)
      );
      nodeDevices = devices.filter(
        device =>
          Array.isArray(device.topoNodeIdList) &&
          device.topoNodeIdList.includes(id)
      );
      defaultControl = nodeInfo.defaultControl || {};
    } else {
      const node = indeNodes.find(indeNode => indeNode.indeNodeId === id);
      nodeInfo = { ...node };
      nodeDevices = devices.filter(
        device =>
          Array.isArray(device.indeNodeIdList) &&
          device.indeNodeIdList?.includes(id)
      );
      defaultControl = nodeInfo.defaultControl || {};
    }
    setDefaultControl(defaultControl);
    setScheduledControls(nodeControls);
    setNodeInfo(nodeInfo);
    setNodeDevices(nodeDevices);
    setChildNodes(childNodes);
  }, [controls, devices, indeNodes, sidePanel, topoNodes]);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: subName,
          pollTypes: [
            {
              type:
                entity === RESOURCE_TYPES.TOPONODE
                  ? POLL_INDIVIDUAL_TOPONODE
                  : POLL_INDIVIDUAL_INDENODE,
              resourceId: id
            },
            {
              type:
                entity === RESOURCE_TYPES.TOPONODE
                  ? POLL_TOPONODE_DEFAULT_CONTROL
                  : POLL_INDENODE_DEFAULT_CONTROL,
              resourceId: id
            },
            {
              type: POLL_DEVICES
            },
            {
              type: POLL_CONTROLS_AND_RESPONSES
            },
            {
              type: POLL_CURVES
            }
          ],
          pollRate
        })
      );
      compileNodePanelInfo();
    } else {
      dispatch(unsubscribe(subName));
    }
    return () => {
      if (isActive) {
        dispatch(unsubscribe(subName));
      }
    };
  }, [compileNodePanelInfo, dispatch, entity, id, isActive]);

  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [isLoading, indeNodes, topoNodes]);

  const hasDefaultControls = defaultControl => {
    if (
      Object.keys(defaultControl).length > 1 ||
      (defaultControl.controlTypes &&
        Object.keys(defaultControl.controlTypes).length)
    ) {
      return true;
    }
    return false;
  };

  const onDeviceSelected = useCallback(
    row => {
      const { sfdi } = row.data;

      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.DEVICE,
          state: SIDEPANEL_STATES.READ,
          sfdi,
          keepBreadcrumbs: true
        })
      );
    },
    [dispatch]
  );

  const onChildNodeSelected = useCallback(
    row => {
      const { topoNodeId } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.TOPONODE,
          state: SIDEPANEL_STATES.READ,
          id: topoNodeId,
          keepBreadcrumbs: true
        })
      );
    },
    [dispatch]
  );

  const onControlSelected = useCallback(
    row => {
      const { controlId } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.CONTROL,
          state: SIDEPANEL_STATES.READ,
          id: controlId,
          keepBreadcrumbs: true
        })
      );
    },
    [dispatch]
  );

  /**
   * Takes a list of properties in a default control and orders it so that the properties
   * display in the correct order in our UI.
   * @param {object} defaultControl - An object with all of the default control's properties
   * except for the control types.
   * @return {object} - ordered list of default control properties.
   */
  const orderDefaultControlProperties = defaultControl => {
    const defaultControlsOrdered = {};
    DEFAULT_CONTROL_FIELDS_LIST_ORDER.forEach(controlAllowed => {
      if (Object.keys(defaultControl).indexOf(controlAllowed) > -1) {
        defaultControlsOrdered[controlAllowed] = defaultControl[controlAllowed];
      }
    });
    return defaultControlsOrdered;
  };

  /**
   * Takes a list of control types in a default control and orders it so that the control types
   * display in the correct order in our UI.
   * @param {object} controlTypes - AN object with all the default control's control types.
   * @return {object} - ordered list of default control types.
   */
  const orderDefaultControlTypes = (controlTypes = {}) => {
    const controlTypesOrdered = {};
    for (let controlType of Object.keys(CONTROL_TYPES)) {
      if (controlTypes.hasOwnProperty(controlType)) {
        controlTypesOrdered[controlType] = controlTypes[controlType];
      }
    }
    return controlTypesOrdered;
  };

  const defaultControlMakeFriendly = defaultControl => {
    const friendlyDefaultControl = {};
    for (let [controlKey, controlValue] of Object.entries(defaultControl)) {
      if (DEFAULT_CONTROL_FIELD_UNIT[controlKey] === TEXTFIELD) {
        //Keep strings/text fields without modification
        friendlyDefaultControl[controlKey] = controlValue;
      } else if (DEFAULT_CONTROL_FIELD_UNIT[controlKey] === 'Hz') {
        //Hz comes in as hundredths of hertz. e.g. value of 500 is 5Hz.
        const convertedDisplayValue = formatNumberWithPrefixAndMultiplier(
          controlValue / 100
        );
        friendlyDefaultControl[
          controlKey
        ] = `${convertedDisplayValue.reducedNumber}${convertedDisplayValue.prefix}${DEFAULT_CONTROL_FIELD_UNIT[controlKey]}`;
      } else if (DEFAULT_CONTROL_FIELD_UNIT[controlKey] === '%setMaxW/s') {
        //Percentage/sec comes in as hundredths of a percent/sec. e.g. value of 500 is 5%setMaxW/s
        if (controlValue === 0) {
          friendlyDefaultControl[controlKey] = 'No Limit';
        } else {
          friendlyDefaultControl[controlKey] = `${controlValue / 100}${
            DEFAULT_CONTROL_FIELD_UNIT[controlKey]
          }`;
        }
      } else if (DEFAULT_CONTROL_FIELD_UNIT[controlKey] === '%') {
        //Percentage comes in as hundredths of a percent. e.g. value of 500 is 5%
        friendlyDefaultControl[controlKey] = `${controlValue / 100}${
          DEFAULT_CONTROL_FIELD_UNIT[controlKey]
        }`;
      } else if (DEFAULT_CONTROL_FIELD_UNIT[controlKey] === 's') {
        //Time field
        friendlyDefaultControl[controlKey] = durationFormatter(
          parseFloat(controlValue)
        );
      } else {
        friendlyDefaultControl[controlKey] = controlValue;
      }
    }
    return friendlyDefaultControl;
  };

  const deviceColumnDefs = useMemo(() => {
    const devicesColumns = [
      {
        headerName: 'ID',
        field: 'deviceId'
      },
      {
        headerName: 'SFDI',
        field: 'sfdi'
      },
      {
        headerName: 'Registration Pin',
        field: 'registrationPin'
      }
    ];
    if (externallyManagedEdevs) {
      devicesColumns.push({
        headerName: 'Remotely Managed',
        field: 'localDevice',
        cellRenderer: ({ value }) => isExternallyManaged(value)
      });
    }
    return devicesColumns;
  }, []);

  if (!isActive || (isActive && isLoading) || nodeInfo === undefined) {
    return null;
  } else {
    //Put the default controls in the correct listing order
    const defaultControlPropertiesOrdered =
      orderDefaultControlProperties(defaultControl);
    //Convert values and display units of default controls
    const defaultControlPropertiesFriendly = derInfoDataConverter(
      defaultControlPropertiesOrdered,
      SEP_RN_DEFAULT_DER_CONTROL
    );
    //Put the default control types in the correct listing order
    const defaultControlTypesOrdered = orderDefaultControlTypes(
      defaultControl.controlTypes || {}
    );
    //Convert values and display units of default control types
    const defaultControlTypesOrderedFriendly = defaultControlMakeFriendly(
      defaultControlTypesOrdered
    );

    const nodeControlsIds = scheduledControls
      ? scheduledControls.map(control => control.controlId)
      : [];

    const filteredControlData = controls.filter(ctrl => {
      return nodeControlsIds.indexOf(ctrl.controlId) > -1;
    });

    const updatedControlsTableData = filteredControlData.map(ctrl => {
      return {
        ...ctrl,
        statusDisplayValue:
          API_FIELD_MAPPINGS.CONTROL_STATUS_DISPLAYS.type[ctrl.status.value]
            .displayText
      };
    });
    const { entity, id } = sidePanel;
    const { localNode: localNodeValue } = nodeInfo;
    const isRemoteResource =
      externallyManagedEdevs && isExternallyManaged(localNodeValue, true);
    const isTopoNode = entity === RESOURCE_TYPES.TOPONODE;
    const parentNodeName = isTopoNode ? 'parentTopoNodeId' : 'parentIndeNodeId';

    return (
      <FullPanel>
        <Panel primaryHeader title="Selected Node Info" />
        <PanelContent>
          <SubPanel>
            <SubPanelRow>
              <TooltipRow
                disabled={isRemoteResource}
                justifyContent="flex-end"
                message={disabledRemoteMessage}
              >
                <EditButton
                  onClick={() =>
                    dispatch(
                      setSidePanel({
                        entity,
                        state: SIDEPANEL_STATES.UPDATE,
                        id,
                        keepBreadcrumbs: true
                      })
                    )
                  }
                  disabled={isRemoteResource}
                >
                  Edit
                </EditButton>
              </TooltipRow>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Description</PanelRowDescriptor>
              <PanelRowDisplay>{nodeInfo.description}</PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>ID</PanelRowDescriptor>
              <PanelRowDisplay>{id}</PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Primacy</PanelRowDescriptor>
              <PanelRowDisplay>{nodeInfo.primacy}</PanelRowDisplay>
            </SubPanelRow>
            {externallyManagedEdevs && (
              <SubPanelRow>
                <PanelRowDescriptor>Remotely Managed</PanelRowDescriptor>
                <PanelRowDisplay>
                  {isRemoteResource ? 'True' : 'False'}
                </PanelRowDisplay>
              </SubPanelRow>
            )}
            <SubPanelRow>
              <PanelRowDescriptor>
                New DER Program List Poll Rate
              </PanelRowDescriptor>
              <PanelRowDisplay>
                {Number.isInteger(nodeInfo.pollRate) &&
                  durationFormatter(parseInt(nodeInfo.pollRate) * 100)}
              </PanelRowDisplay>
            </SubPanelRow>
            {isTopoNode && (
              <SubPanelRow>
                <PanelRowDescriptor>Parent Node</PanelRowDescriptor>
                <PanelRowDisplay>{nodeInfo.parentTopoNodeId}</PanelRowDisplay>
              </SubPanelRow>
            )}
          </SubPanel>
          <Panel title="Controls">
            <SubPanel>
              <SubPanelRow>
                <SubPanelHeader>
                  <SectionHeader header="Default Control" />
                </SubPanelHeader>
                <TooltipRow
                  disabled={isRemoteResource}
                  justifyContent="flex-end"
                  message="Remote nodes may not be modified"
                >
                  <EditButton
                    onClick={() =>
                      dispatch(
                        setSidePanel({
                          entity: RESOURCE_TYPES.CONTROL,
                          state: SIDEPANEL_STATES.UPDATE,
                          keepBreadcrumbs: true,
                          data: { [parentNodeName]: id }
                        })
                      )
                    }
                    disabled={isRemoteResource}
                  >
                    {hasDefaultControls(defaultControl) ? 'Edit' : 'Add'}
                  </EditButton>
                </TooltipRow>
              </SubPanelRow>
            </SubPanel>
            {Object.keys(defaultControlPropertiesFriendly).length > 0 ||
            Object.keys(defaultControlTypesOrderedFriendly).length > 0 ? (
              <SubPanel>
                {defaultControlPropertiesFriendly.map(
                  ({ deviceProperty, value }) => {
                    return (
                      <SubPanelRow key={deviceProperty}>
                        <PanelRowDescriptor alignSelf="flex-start">
                          {deviceProperty}
                        </PanelRowDescriptor>
                        <PanelRowDisplay>{value}</PanelRowDisplay>
                      </SubPanelRow>
                    );
                  }
                )}

                {Object.keys(defaultControlTypesOrderedFriendly).length > 0 ? (
                  <>
                    <SectionHeader header="Default Control Mode(s)" />
                    <ControlFieldsContainer
                      controlTypes={defaultControlTypesOrderedFriendly}
                    />
                  </>
                ) : (
                  ''
                )}
              </SubPanel>
            ) : (
              <SubPanel>
                <SubPanelRow>
                  <PanelRowDescriptor>None found</PanelRowDescriptor>
                </SubPanelRow>
              </SubPanel>
            )}
            <Break />
            <SubPanel>
              <SubPanelRow>
                <SubPanelHeader>
                  <SectionHeader header="Scheduled Controls" />
                </SubPanelHeader>
              </SubPanelRow>
              <Table
                rowData={updatedControlsTableData}
                columnDefs={controlColumnDefs}
                height="200px"
                width="100%"
                padding="0px"
                margin="10px 0px"
                onRowClicked={onControlSelected}
              />
            </SubPanel>
            <Break margin="8px 0" />
            <SubPanel>
              <AddRelationsContainer title="Add Scheduled Control">
                <TooltipRow
                  disabled={isRemoteResource}
                  message={disabledRemoteMessage}
                >
                  <AddButton
                    onClick={() =>
                      dispatch(
                        setSidePanel({
                          entity: RESOURCE_TYPES.CONTROL,
                          state: SIDEPANEL_STATES.CREATE,
                          keepBreadcrumbs: true,
                          data: { [parentNodeName]: id }
                        })
                      )
                    }
                    disabled={isRemoteResource}
                  >
                    + New Control
                  </AddButton>
                </TooltipRow>
              </AddRelationsContainer>
            </SubPanel>
          </Panel>
          {isTopoNode && (
            <Panel title="Child Nodes">
              <SubPanel>
                <Table
                  rowData={childNodes}
                  columnDefs={childNodeColumnDefs}
                  height="200px"
                  width="100%"
                  fontSize="10px"
                  padding="0px"
                  margin="10px 0px"
                  onRowClicked={onChildNodeSelected}
                />
              </SubPanel>
              <Break margin="8px 0" />
              <SubPanel>
                <AddRelationsContainer title="Add Child Node">
                  <TooltipRow
                    disabled={isRemoteResource}
                    message={disabledRemoteMessage}
                  >
                    <AddButton
                      buttonWidth="95px"
                      margin="0 4px 0 0"
                      onClick={() =>
                        dispatch(
                          setSidePanel({
                            entity: RESOURCE_TYPES.TOPONODE,
                            state: SIDEPANEL_STATES.ADD_EXISTING,
                            keepBreadcrumbs: true,
                            data: { [parentNodeName]: id }
                          })
                        )
                      }
                      disabled={isRemoteResource}
                    >
                      Find Existing
                    </AddButton>
                    <AddButton
                      buttonWidth="55px"
                      margin="0 0 0 4px"
                      onClick={() =>
                        dispatch(
                          setSidePanel({
                            entity: RESOURCE_TYPES.TOPONODE,
                            state: SIDEPANEL_STATES.CREATE,
                            keepBreadcrumbs: true,
                            data: { [parentNodeName]: id }
                          })
                        )
                      }
                      disabled={isRemoteResource}
                    >
                      + New
                    </AddButton>
                  </TooltipRow>
                </AddRelationsContainer>
              </SubPanel>
            </Panel>
          )}
          <Panel title="Associated Devices">
            <SubPanel>
              <Table
                rowData={nodeDevices}
                columnDefs={deviceColumnDefs}
                height="200px"
                width="100%"
                fontSize="10px"
                padding="0px"
                margin="10px 0px"
                onRowClicked={onDeviceSelected}
              />
            </SubPanel>
            <Break margin="8px 0" />
            <SubPanel>
              <AddRelationsContainer title="Add Device">
                <TooltipRow
                  disabled={isRemoteResource}
                  message={disabledRemoteMessage}
                >
                  <AddButton
                    buttonWidth="95px"
                    margin="0 4px 0 0"
                    onClick={() =>
                      dispatch(
                        setSidePanel({
                          entity: RESOURCE_TYPES.DEVICE,
                          state: SIDEPANEL_STATES.ADD_EXISTING,
                          keepBreadcrumbs: true,
                          data: { [parentNodeName]: id }
                        })
                      )
                    }
                    disabled={isRemoteResource}
                  >
                    Find Existing
                  </AddButton>
                  <AddButton
                    buttonWidth="55px"
                    margin="0 0 0 4px"
                    onClick={() =>
                      dispatch(
                        setSidePanel({
                          entity: RESOURCE_TYPES.DEVICE,
                          state: SIDEPANEL_STATES.CREATE,
                          keepBreadcrumbs: true,
                          data: { [parentNodeName]: id }
                        })
                      )
                    }
                    disabled={isRemoteResource}
                  >
                    + New
                  </AddButton>
                </TooltipRow>
              </AddRelationsContainer>
            </SubPanel>
          </Panel>
        </PanelContent>
        <PanelButtonRow>
          <BlueBorderButton
            onClick={() =>
              determineBack(
                sidePanel,
                () => dispatch(revertSidePanel()),
                () => dispatch(closeSidePanel())
              )
            }
          >
            Back
          </BlueBorderButton>
        </PanelButtonRow>
      </FullPanel>
    );
  }
};

export default NodePanel;
